import { get } from 'lodash';
import { ActionsObservable, combineEpics, StateObservable } from 'redux-observable';
import { Subject } from 'rxjs/Subject';
import { ignoreElements, map, mergeMap } from 'rxjs/operators';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';

// services
import AnalyticsService from './analytics-provider.service';

// types
import {
    IClickEventData,
    IEpicCreatorCallback,
    IWidgetAnalyticsInfo,
    IWidgetLanguageAttributes,
} from 'types/analytics';
import { IEpic } from 'types/epic';
import { IAction } from 'types/action';
import { IModule } from '@sitegen/common/types';

export const initAnalyticsObservable$: Subject<boolean> = new Subject();

export const epic$ = new BehaviorSubject(combineEpics(...[]));

export const createEmptyAnalyticsEpics = (): IEpic => {
    return (action$, store) => epic$.pipe(mergeMap(epic => epic(action$, store, {})));
};

export const loadAnalyticsEpicsAsync = (importPromise: Promise<IModule>): void => {
    importPromise.then(analyticsEpics => {
        epic$.next(analyticsEpics.default);
    });
};

export const analyticsEpicCreator = (
    types: string[],
    callback: IEpicCreatorCallback
) => (
    action$: ActionsObservable<IAction>,
    /* eslint-disable-next-line*/
    store$: StateObservable<any>
) =>
    action$
        .ofType(...types)
        .pipe(
            map(action => {
                callback(action, store$);
            })
        )
        .pipe(ignoreElements());

export const mainPage = (actionTypes: string[], widgetAnalyticsInfo: IWidgetAnalyticsInfo): IEpic =>
    analyticsEpicCreator(
        actionTypes,
        (): void => {
            AnalyticsService.updateAnalyticalData({
                flowStep: widgetAnalyticsInfo.homePage.name,
            });
            AnalyticsService.sendPageEvent({
                pageName: widgetAnalyticsInfo.homePage.name,
                siteName: widgetAnalyticsInfo.siteName,
                siteSection: widgetAnalyticsInfo.siteSection,
                siteSubSection: widgetAnalyticsInfo.homePage.subSection,
                ubisoftId: '',
                platformSelection: '',
                errorType: ''
            });
        }
    );

export const setLanguage = (actionTypes: string[], widgetLanguageAttributes: IWidgetLanguageAttributes): IEpic =>
    analyticsEpicCreator(
        actionTypes,
        (action: IAction): void => {
            const language = get(action, 'payload.language');
            const country = get(action, 'payload.country');

            AnalyticsService.updateAnalyticalData({
                language,
                country
            });

            /*
             * There is a requirement to send a click event on every click which changes the language,
             * but it's impossible to put a click handler on a option tag through html attributes,
             * so we have to do it manually via analytics method
             */
            AnalyticsService.sendClickEvent({
                location: widgetLanguageAttributes['data-aa-location'],
                category: widgetLanguageAttributes['data-aa-category'],
                action: widgetLanguageAttributes.changeLanguageAction(language)
            });
        }
    );

export const setUserAnalyticsData = (actionTypes: string[]): IEpic =>
    analyticsEpicCreator(
        actionTypes,
        (action: IAction): void => {
            const userId = get(action, 'payload.UbisoftConnectProfile.user.userId');

            AnalyticsService.updateAnalyticalData({
                errorType: '',
                ubisoftId: userId,
            });
        }
    );

export const cleanLoginAnalyticsData = (actionTypes: string[]): IEpic =>
    analyticsEpicCreator(
        actionTypes,
        (): void => {
            AnalyticsService.updateAnalyticalData({
                errorType: '',
                ubisoftId: '',
            });
        }
    );

export const setPlatformSelection = (actionTypes: string[]): IEpic =>
    analyticsEpicCreator(
        actionTypes,
        (action: IAction): void => {
            const selectedPlatform = get(action, 'payload.platform');

            AnalyticsService.updateAnalyticalData({
                platformSelection: selectedPlatform.toUpperCase(),
                errorType: ''
            });
        }
    );

export const setAdditionalInfo = (actionTypes: string[]): IEpic =>
    analyticsEpicCreator(
        actionTypes,
        (action: IAction<IClickEventData>): void => {
            const analyticsData = get(action, 'payload') as IClickEventData;

            AnalyticsService.sendClickEvent(analyticsData);
        }
    );
